import { assign, setup, sendParent } from 'xstate';
import type { ClientDomainEvent } from '@/features/client/state/events';
import type { StatementDomainEvent } from '@/features/statement/state/events';
import type { TaxrulesetDomainEvent } from '@/features/taxruleset/state/events';
import type { FilingcredentialDomainEvent } from '@/features/filingcredential/state/events';
import type { ApprovalDomainEvent } from '@/features/approval/state/events';
import type { FilingEvent } from './events';
import {
  correctFilingActor,
  correctRejectedFilingActor,
  findFirmFilingsActor,
  openFilingActor,
  viewFilingActor,
} from './filing.actors';

type FilingEvents =
  | FilingEvent
  | ClientDomainEvent
  | StatementDomainEvent
  | TaxrulesetDomainEvent
  | FilingcredentialDomainEvent
  | ApprovalDomainEvent;
type FilingContext = {
  filings: Array<{ filingId: string; status?: string | null }>;
  review: { filingId: string } | null;
  selectedFilingId: string | null;
  error: unknown;
};

export const filingMachine = setup({
  types: { context: {} as FilingContext, events: {} as FilingEvents },
  actors: {
    findFirmFilingsActor,
    viewFilingActor,
    openFilingActor,
    correctFilingActor,
    correctRejectedFilingActor,
  },
  actions: {
    reportError: assign({ error: ({ event }) => event }),
    clearError: assign({ error: () => null }),
    selectReview: assign({
      selectedFilingId: ({ event }) => (event.type === 'filing.load-review' ? event.filingId : null),
    }),
    emitOpened: sendParent(({ context }) => ({ type: 'filing.opened', filingId: context.selectedFilingId ?? '' })),
    emitCorrected: sendParent(({ context }) => ({
      type: 'filing.corrected',
      filingId: context.selectedFilingId ?? '',
    })),
    emitRejectedCorrected: sendParent(({ context }) => ({
      type: 'filing.rejected-corrected',
      filingId: context.selectedFilingId ?? '',
    })),
  },
}).createMachine({
  id: 'filing',
  initial: 'idle',
  context: { filings: [], review: null, selectedFilingId: null, error: null },
  on: {
    'filing.load-filings': { target: '.loading', actions: 'clearError' },
    'filing.load-review': { target: '.reviewLoading', actions: 'selectReview' },
    'filing.open': { target: '.opening', actions: 'clearError' },
    'filing.correct': { target: '.correcting', actions: 'selectReview' },
    'filing.correct-rejected': { target: '.correctingRejected', actions: 'selectReview' },
    'client.registered': { target: '.loading' },
    'taxruleset.published': { target: '.loading' },
    'filingcredential.registered': { target: '.loading' },
    'statement.handed-in': { target: '.loading' },
    'approval.recorded': { target: '.loading' },
  },
  states: {
    idle: {},
    loading: {
      invoke: {
        src: 'findFirmFilingsActor',
        onDone: { target: 'ready', actions: assign({ filings: ({ event }) => event.output }) },
        onError: { target: 'error', actions: 'reportError' },
      },
    },
    ready: {},
    error: {},
    reviewLoading: {
      invoke: {
        src: 'viewFilingActor',
        input: ({ context }) => context.selectedFilingId ?? '',
        onDone: { target: 'reviewReady', actions: assign({ review: ({ event }) => event.output ?? null }) },
        onError: { target: 'reviewError', actions: 'reportError' },
      },
    },
    reviewReady: {},
    reviewError: {},
    opening: {
      invoke: {
        src: 'openFilingActor',
        input: ({ event }) =>
          event.type === 'filing.open'
            ? {
                clientId: event.clientId,
                taxRuleSetId: event.taxRuleSetId,
                filingCredentialId: event.filingCredentialId,
              }
            : { clientId: '', taxRuleSetId: '', filingCredentialId: '' },
        onDone: {
          target: 'loading',
          actions: [
            assign({ selectedFilingId: ({ event }) => event.output }),
            'emitOpened',
            sendParent({ type: 'ui.toast.push', toast: { title: 'Filing opened', tone: 'success' } }),
          ],
        },
        onError: {
          target: 'ready',
          actions: [
            'reportError',
            sendParent({ type: 'ui.toast.push', toast: { title: 'Filing could not be opened', tone: 'error' } }),
          ],
        },
      },
    },
    correcting: {
      invoke: {
        src: 'correctFilingActor',
        input: ({ event }) =>
          event.type === 'filing.correct'
            ? { filingId: event.filingId, correctionNote: event.correctionNote }
            : { filingId: '', correctionNote: '' },
        onDone: {
          target: 'loading',
          actions: [
            'emitCorrected',
            sendParent({ type: 'ui.toast.push', toast: { title: 'Correction submitted', tone: 'success' } }),
          ],
        },
        onError: {
          target: 'reviewReady',
          actions: [
            'reportError',
            sendParent({ type: 'ui.toast.push', toast: { title: 'Correction could not be submitted', tone: 'error' } }),
          ],
        },
      },
    },
    correctingRejected: {
      invoke: {
        src: 'correctRejectedFilingActor',
        input: ({ event }) =>
          event.type === 'filing.correct-rejected'
            ? { filingId: event.filingId, correctionNote: event.correctionNote }
            : { filingId: '', correctionNote: '' },
        onDone: {
          target: 'loading',
          actions: [
            'emitRejectedCorrected',
            sendParent({ type: 'ui.toast.push', toast: { title: 'Rejected correction submitted', tone: 'success' } }),
          ],
        },
        onError: {
          target: 'reviewReady',
          actions: [
            'reportError',
            sendParent({ type: 'ui.toast.push', toast: { title: 'Correction could not be submitted', tone: 'error' } }),
          ],
        },
      },
    },
  },
});
